/**
 * Reference-data lookups the promo builder needs (product search, categories, sales channels,
 * customer groups). These BFF endpoints are already consumed by `features/catalog` — FSD forbids
 * importing another feature, so this copies the endpoint paths/shapes (not the code) per
 * plan-011 §6 "Data plumbing note".
 */
import { apiClient } from '@/shared/api';

import type { CategoryRef, CustomerGroupRef, Page, ProductRef, SalesChannelRef } from '../model/types';

// ── Products (search-as-you-type for ProductAutocomplete / simulator basket) ──────────────────
export interface SearchProductsParams {
  query?: string;
  page?: number;
  size?: number;
}

export const searchProducts = async (params: SearchProductsParams): Promise<Page<ProductRef>> =>
  (
    await apiClient.post<Page<ProductRef>>('/api/v1/catalog/products/search', {
      query: params.query,
      pagination: { page: params.page ?? 0, size: params.size ?? 20 },
    })
  ).data;

/**
 * Resolve one product's display name by id — used to back-fill a STATIC product set's entries
 * (which only carry `referenceId`/`referenceCode`, never a name) so the editor never has to fall
 * back to showing a raw UUID chip. Returns `undefined` on any failure (e.g. the product was since
 * deleted) so the caller can fall back to the id/code it already has instead of throwing.
 */
export const getProductRef = async (productId: string): Promise<ProductRef | undefined> => {
  try {
    return (await apiClient.get<ProductRef>(`/api/v1/catalog/products/${productId}`)).data;
  } catch {
    return undefined;
  }
};

// ── Categories (FILTER product sets) ───────────────────────────────────────────────────────────
export const listAllCategories = async (): Promise<CategoryRef[]> =>
  (
    await apiClient.post<Page<CategoryRef>>('/api/v1/catalog/categories', {
      pagination: { page: 0, size: 200 },
    })
  ).data.content;

// ── Sales channels (condition CHANNEL_IN, promotion restrictions) ─────────────────────────────
export const listSalesChannels = async (): Promise<SalesChannelRef[]> =>
  (await apiClient.get<SalesChannelRef[]>('/api/v1/catalog/pricing/sales-channels')).data;

// ── Customer groups (condition CUSTOMER_IN_GROUP, promotion restrictions) ──────────────────────
export const listCustomerGroups = async (): Promise<CustomerGroupRef[]> =>
  (await apiClient.get<CustomerGroupRef[]>('/api/v1/catalog/pricing/customer-groups')).data;
